import React, { useEffect, useState } from 'react';
import styles from 'src/app/components/DrawGroupModal/DrawPopup/DrawPopup.module.scss';
import { drawenLayerGroupService, drawenLayerService } from 'src/app/services';

import { drawenLayerGroupHelper, statusHelper } from 'src/app/helpers';
import { DrawenLayerGroupResultType } from 'src/types/components/draw-module.type';
import DraggableModal from 'src/app/components/DraggableModal/DraggableModal';
import { DrawController } from 'src/controllers';
import { DrawenLayerCreateType } from 'src/app/helpers/drawenLayer.helper';
import { layerToGeojson } from 'src/controllers/config';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import { useForm } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Switch from 'antd/es/switch';
import Divider from 'antd/es/divider';
import Space from 'antd/es/space';
import Select from 'antd/es/select';

interface IProps {
  drawController: DrawController | undefined;
  tarafId: number | undefined;
  activeLayerOnDraw: any;
  setActiveLayerOnDraw: React.Dispatch<React.SetStateAction<any>>;
}

enum ArrowFillType {
  DISABLED = 'disabled',
  NONE = 'none',
  FILL = 'fill',
}

// Arrow types enum
enum ArrowType {
  NONE = 'none',
  ARROW_HEADS = 'arrowHeads',
  START_END_ARROW = 'startEndArrow',
}

export default function DrawPopup({
  drawController,
  tarafId,
  activeLayerOnDraw,
  setActiveLayerOnDraw,
  drawGroups,
  setDrawGroups,
  setRotateData,
}: IProps & {
  drawGroups: DrawenLayerGroupResultType[];
  setDrawGroups: React.Dispatch<React.SetStateAction<DrawenLayerGroupResultType[]>>;
  setRotateData: React.Dispatch<React.SetStateAction<any>>;
}) {
  const { t } = useTranslation();

  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [name, setName] = useState('');
  const [isDashed, setIsDashed] = useState(false);
  const [arrowType, setArrowType] = useState<ArrowType>(ArrowType.NONE);
  const [displayModal, setDisplayModal] = useState<boolean>(false);
  const [arrowFillType, setArrowFillType] = useState<ArrowFillType>(ArrowFillType.NONE);

  const [newText, setNewText] = useState('');
  const [newIsPrivate, setNewIsPrivate] = useState(false);

  const [form] = useForm<{
    text: string;
    isPrivate: boolean;
  }>();

  const getData = async () => {
    const res = await drawenLayerGroupHelper.getDrawGroups(tarafId);

    if (res) {
      setDrawGroups(res.data);
    }
  };

  useEffect(() => {
    getData();
  }, [tarafId]);

  useEffect(() => {
    if (activeLayerOnDraw.options.type === 'text') {
      setName(activeLayerOnDraw.options.style.text);
    }
  }, [activeLayerOnDraw]);
  const onSave = async () => {
    try {
      if (activeLayerOnDraw) {
        if (selectedGroups.length === 0) {
          statusHelper.displayErrorMessage(t('hymotsc.drawTool.chooseAtLeastOneGroup'));
          return;
        }

        if (!name.trim()) {
          statusHelper.displayErrorMessage(t('hymotsc.drawTool.pleaseEnterNameForDrawing'));
          return;
        }

        const geoJson = layerToGeojson(activeLayerOnDraw);
        const marker = activeLayerOnDraw as L.Marker;

        // Handle text marker style
        if (marker.options.type === 'text' && marker.options.style) {
          const { text, color, fontSize } = marker.options.style;

          geoJson.properties.text = text;
          geoJson.properties.color = color;
          geoJson.properties.fontSize = fontSize;
          geoJson.properties.isLabel = true;
          geoJson.properties.zoomLevel = drawController?.map.getZoom();
        }

        // Apply dashed style
        if (isDashed) {
          geoJson.properties.style = {
            ...(geoJson.properties.style || {}),
            dashArray: '5, 10',
          };
        }

        // Apply arrow styles
        if (geoJson.geometry && geoJson.geometry.type === 'LineString' && activeLayerOnDraw instanceof L.Polyline) {
          // Apply arrowheads if selected
          if (arrowType === ArrowType.ARROW_HEADS) {
            activeLayerOnDraw.arrowheads({ size: '10px', frequency: '60px' });

            geoJson.properties.arrowHead = {
              size: '10px',
              frequency: '60px',
            };
          }
          // Apply start-end arrow if selected
          else if (arrowType === ArrowType.START_END_ARROW) {
            geoJson.properties.startEndArrow = true;
          }
          geoJson.properties.arrowFillType = arrowFillType === ArrowFillType.NONE ? false : arrowFillType;
        }

        const payload: DrawenLayerCreateType = {
          name,
          drawenLayerGroupIds: selectedGroups,
          features: geoJson,
          tarafId,
        };
        const { data: newItem } = await drawenLayerService.drawenLayerCreate(payload);

        drawController?.createLayer(newItem, selectedGroups);

        setSelectedGroups([]);
        setName('');
        setActiveLayerOnDraw(null);
        setIsDashed(false);
        setArrowType(ArrowType.NONE);
        setArrowFillType(ArrowFillType.NONE);
        drawController?.setIsOpenPopupDrawController(false);
        statusHelper.displaySuccessStatusMessage(t('hymotsc.drawTool.drawingSavedSuccessfully'));
      }
    } catch (error) {
      console.log('error', error);
      statusHelper.displayErrorMessage(t('hymotsc.drawTool.failedToSaveDrawing'));
    } finally {
      await getData();
      setRotateData(undefined);
    }
  };

  const onCancel = () => {
    drawController?.cancelCreateEvent();
    setRotateData(undefined);
  };

  const onFormSave = async () => {
    try {
      const { text, isPrivate } = form.getFieldsValue();

      if (!text || !text.trim()) {
        statusHelper.displayErrorMessage(t('hymotsc.drawTool.pleaseEnterNameForDrawing'));
        return;
      }

      if (drawGroups.some((g) => g.text === text)) {
        statusHelper.displayErrorMessage(t('hymotsc.drawTool.layerTextMustDifferentThanOthers'));
        return;
      }

      const { data: newItem } = await drawenLayerGroupService.drawenLayerGroupCreate({
        text,
        isPrivate,
      });

      setDrawGroups((current) => (current.some((g) => g.id === newItem.id) ? current : [...current, newItem]));
      drawController?.setDrawGroupsData([...drawGroups, newItem]);

      statusHelper.displaySuccessStatusMessage(t('hymotsc.drawTool.layerCreatedSuccessfully'));
      setDisplayModal(false);
      form.resetFields();
    } catch (err) {
      console.error(err);
      statusHelper.displayErrorMessage(t('hymotsc.drawTool.failedToCreateLayer'));
    }
  };

  const handleCancel = () => {
    setDisplayModal(false);
  };

  const handleInlineSave = async () => {
    if (!newText.trim()) {
      statusHelper.displayErrorMessage(t('hymotsc.drawTool.enterALayerName'));
      return;
    }
    if (drawGroups.some((g) => g.text === newText)) {
      statusHelper.displayErrorMessage(t('hymotsc.drawTool.layerTextMustDifferentThanOthers'));
      return;
    }
    try {
      const { data: newItem } = await drawenLayerGroupService.drawenLayerGroupCreate({
        text: newText,
        isPrivate: newIsPrivate,
      });
      setDrawGroups((curr) => {
        if (curr.some((g) => g.id === newItem.id)) return curr;
        const updated = [...curr, newItem];
        drawController?.setDrawGroupsData(updated);
        return updated;
      });
      setSelectedGroups((prev) => (prev.includes(newItem.id) ? prev : [...prev, newItem.id]));
      setNewText('');
      setNewIsPrivate(false);
      statusHelper.displaySuccessStatusMessage(t('hymotsc.drawTool.layerCreatedSuccessfully'));
    } catch {
      statusHelper.displayErrorMessage(t('hymotsc.drawTool.failedToCreateLayer'));
    }
  };

  const createLayerModal = () => (
    <DraggableModal
      headerText={t('hymotsc.drawTool.addNewDrawing')}
      className={styles.drag}
      contentClassName={styles.dragContent}
      position={{ x: 400, y: 30 }}
      onClickClose={() => setDisplayModal(false)}
      id="drawGroupsModal"
      style={{ width: '450px', height: '200px' }}
    >
      <div className="popup-input-container" style={{ margin: '1rem' }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            text: '',
            isPrivate: false,
          }}
          onFinish={onFormSave}
          id="drawPopup"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Form.Item
              label="Name"
              name="text"
              rules={[{ required: false, message: t('hymotsc.drawTool.enterALayerName') || '' }]}
            >
              <Input placeholder="Layer name" style={{ border: '1px solid #4f4f4f', backgroundColor: '#252526' }} />
            </Form.Item>

            <Form.Item label="Visibility" name="isPrivate" valuePropName="checked">
              <Switch checkedChildren="Private" unCheckedChildren="Public" className="custom-switch" />
            </Form.Item>

            <div style={{ textAlign: 'right' }}>
              <button type="button" onClick={handleCancel} style={{ marginRight: 8 }} className="modal-button">
                {t('general.cancel')}
              </button>
              <button type="button" className="next-button" onClick={onFormSave}>
                {t('general.save')}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </DraggableModal>
  );

  return (
    <DraggableModal
      id="draw-popup"
      headerText={t('hymotsc.drawTool.addNewDrawing')}
      className={styles.drag}
      position={{
        x: 100,
        y: 100,
      }}
      contentClassName={styles.dragContent}
      onClickClose={onCancel}
      style={{ width: '400px', height: '100%' }}
    >
      {/* {displayModal && createLayerModal()} */}
      <div className="area-select-units-select-agents-container">
        <div className="popup-input-container" style={{ margin: '1rem' }}>
          <span className="popup-input-title">{t('hymotsc.drawTool.selectLayer')}</span>
          <SearchableSelect
            className={styles.searchableSelect}
            notSorted
            value={selectedGroups}
            onChange={setSelectedGroups}
            mode="multiple"
            style={{ width: '100%', border: '1px solid #4f4f4f' }}
            showArrow
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: '8px 0', backgroundColor: '#fff' }} />
                <Space direction="vertical" style={{ padding: '10px', width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Input
                      size="middle"
                      placeholder={t('hymotsc.drawTool.newLayerName') || ''}
                      value={newText}
                      onChange={(e) => setNewText(e.target.value)}
                      style={{ flex: 1, width: '100%', border: '1px solid #4f4f4f', fontSize: '13px' }}
                    />

                    <Switch
                      className="custom-switch"
                      size="default"
                      checked={newIsPrivate}
                      onChange={setNewIsPrivate}
                      checkedChildren={t('hymotsc.drawTool.private')}
                      unCheckedChildren={t('hymotsc.drawTool.public')}
                    />
                  </div>
                  <button type="button" className="next-button" onClick={handleInlineSave} style={{ width: '100%' }}>
                    {t('hymotsc.drawTool.createNewLayer')}
                  </button>
                </Space>
              </>
            )}
          >
            {drawGroups.map((o) => (
              <Select.Option key={o.id} value={o.id} className={styles.selectOption}>
                {o.text}
              </Select.Option>
            ))}
          </SearchableSelect>

          {activeLayerOnDraw.options.type !== 'text' && (
            <div>
              <span className="popup-input-title"> {t('hymotsc.drawTool.enterName')}</span>
              <Input onChange={(e) => setName(e.target.value)} placeholder={t('hymotsc.drawTool.name') || ''} />
            </div>
          )}

          {/* Dashed Switch */}
          {activeLayerOnDraw.options.color &&
            activeLayerOnDraw.options.type !== 'text' &&
            !activeLayerOnDraw.options.icon && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '1rem',
                }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#ffffff',
                    height: '100%',
                    alignSelf: 'center',
                    marginRight: '8px',
                    width: '50%',
                  }}
                >
                  {t('hymotsc.drawTool.dashedLine')}
                </span>
                <Switch
                  style={{ width: '50%' }}
                  className="custom-switch"
                  checkedChildren={t('general.yes')}
                  unCheckedChildren={t('general.no')}
                  checked={isDashed}
                  onChange={(checked) => setIsDashed(checked)}
                />
              </div>
            )}
          {/* Arrow Type Selector */}
          {activeLayerOnDraw instanceof L.Polyline && activeLayerOnDraw.toGeoJSON().geometry.type === 'LineString' && (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  margin: '1rem',
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#fff',
                    width: '50%',
                  }}
                >
                  {t('hymotsc.drawTool.arrowStyle')}
                </span>

                <Select
                  value={arrowType}
                  onChange={(val) => setArrowType(val)}
                  style={{ width: '50%', border: '1px solid #4f4f4f' }}
                >
                  <Select.Option value={ArrowType.NONE}>{t('hymotsc.drawTool.none')}</Select.Option>
                  <Select.Option value={ArrowType.ARROW_HEADS}>{t('hymotsc.drawTool.multipleArrows')}</Select.Option>
                  <Select.Option value={ArrowType.START_END_ARROW}>
                    {t('hymotsc.drawTool.startEndArrows')}
                  </Select.Option>
                </Select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '1rem' }}>
                <span style={{ paddingTop: 10 }} className="popup-input-title">
                  {t('hymotsc.drawTool.arrowFillType')}
                </span>
                <SearchableSelect
                  style={{
                    width: '50%',
                    border: arrowType === ArrowType.NONE ? '1px solid rgba(79, 79, 79, 0.5)' : '1px solid #4f4f4f',
                  }}
                  notSorted
                  value={arrowType === ArrowType.NONE ? ArrowFillType.DISABLED : arrowFillType}
                  onChange={(v) => setArrowFillType(v as ArrowFillType)}
                  allowClear={false}
                  disabled={arrowType === ArrowType.NONE}
                >
                  {arrowType === ArrowType.NONE && (
                    <Select.Option value={ArrowFillType.DISABLED} disabled>
                      {t('hymotsc.drawTool.unavailable')}
                    </Select.Option>
                  )}
                  <Select.Option value={ArrowFillType.NONE}>{t('hymotsc.drawTool.standardArrow')}</Select.Option>
                  <Select.Option value={ArrowFillType.FILL}>{t('hymotsc.drawTool.filledArrow')}</Select.Option>
                </SearchableSelect>
              </div>
            </div>
          )}

          <div className="popup-footer" style={{ padding: 0, margin: '1rem' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <button type="button" className="modal-button" onClick={onCancel}>
                {t('general.cancel')}
              </button>
              <button type="button" className="next-button" onClick={onSave}>
                {t('general.save')}
              </button>
            </Space>
          </div>
        </div>
      </div>
    </DraggableModal>
  );
}
